import { CalendarClock } from "lucide-react";
import { etichettaIpotesiData, isMesePlaceholder } from "../../utils/dataIpotesi";

/**
 * La data di una riga nelle timeline di Sede, Cliente e Dispositivo.
 *
 * ## Il problema che risolve
 *
 * Le tre timeline collassano DUE colonne in un valore solo —
 * `data_effettiva ?? ipotesi_data` sulle installazioni, `data_effettiva ??
 * data_attivazione` sulle attivazioni, `data_completamento ?? data_prevista`
 * sugli interventi — e quel `??` butta via l'informazione più importante che
 * uno storico possa dare: **se la cosa è successa davvero**.
 *
 * Il risultato era che "3 lug 2026" poteva voler dire "ci siamo andati il 3
 * luglio" oppure "contiamo di andarci il 3 luglio", e le due erano
 * indistinguibili. In una pagina che si chiama storico, la seconda letta come
 * la prima è un fatto inventato.
 *
 * Era anche il motivo per cui la regola del segnaposto di mese (#78) qui non si
 * poteva applicare: non sapendo da quale campo veniva il valore, non si poteva
 * sapere se il primo del mese fosse un segnaposto o un giorno vero.
 *
 * ## Cosa conta come "stimata"
 *
 * Lo decide il chiamante, perché la regola non è la stessa per tutti:
 *
 *  - installazioni, attivazioni, interventi -> ha vinto il ramo di FALLBACK
 *    della cascata, cioè il campo della data prevista. Vale anche per una data
 *    passata: un'installazione prevista a giugno e mai confermata resta una
 *    previsione, non diventa un fatto perché nel frattempo giugno è passato.
 *  - sopralluoghi -> hanno un campo solo (`data_sopralluogo`, NOT NULL), quindi
 *    la cascata non c'è e il campo non dice niente. Lì l'unica cosa che
 *    distingue una previsione da un fatto è il calendario: è stimata se la data
 *    è nel FUTURO.
 *
 * ## Come si vede
 *
 * Stesso vocabolario della pila "Da pianificare": icona di calendario con
 * orologio più testo in corsivo attenuato. Chi guarda impara il segno una volta
 * e lo ritrova ovunque significhi "questa data non è (ancora) un fatto".
 */
export function DataStorico({
  data,
  stimata,
  daIpotesiData = false,
  stato,
  className = "",
}: {
  data: string | null;
  /** true se il valore viene dal campo della data PREVISTA, non di quella avvenuta. */
  stimata: boolean;
  /**
   * true SOLO quando il valore viene da `installation_schedule.ipotesi_data`,
   * l'unica colonna in cui il primo del mese è un segnaposto invece che un
   * giorno (regola della #78).
   *
   * È separato da `stimata` di proposito, e la distinzione non è teorica: la
   * data di un sopralluogo è "stimata" quando è nel futuro, ma resta un giorno
   * concordato — un sopralluogo fissato per il 1° settembre va mostrato
   * "1 set 2026", non "Settembre 2026". Legarlo a `stimata` trasformava un
   * appuntamento preso in un mese generico.
   */
  daIpotesiData?: boolean;
  /**
   * Lo stato del record sorgente, quando la riga della timeline ce l'ha (le tre
   * pagine lo mostrano gia' nel Badge accanto a questa data).
   *
   * Serve alla stessa eccezione applicata al calendario: dal momento in cui
   * un'installazione e' "Programmata", il primo del mese e' un giorno scelto e
   * non piu' un promemoria di mese. Senza, uno storico continuerebbe a
   * mostrare "Settembre 2026" per un'installazione fissata al 1° settembre e
   * poi effettuata quel giorno.
   *
   * Il vocabolario degli stati e' diverso fra i quattro moduli (un sopralluogo
   * e' "Approvato", un'attivazione "Cessata"), ma qui non e' un problema:
   * `daIpotesiData` e' vero SOLO per le installazioni, quindi lo stato viene
   * consultato solo dove quelle parole significano qualcosa.
   */
  stato?: string | null;
  className?: string;
}) {
  if (!data) {
    return <span className={`text-sm text-ink/40 ${className}`}>—</span>;
  }

  // Serve che siano vere entrambe: il campo giusto E il ramo previsionale della
  // cascata. Su una `data_effettiva` il primo del mese è un giorno in cui
  // qualcuno è andato davvero sul posto, e renderlo "Settembre 2026"
  // cancellerebbe un fatto.
  const segnaposto = stimata && daIpotesiData && isMesePlaceholder(data, stato);
  const testo = segnaposto
    ? etichettaIpotesiData(data, stato)
    : new Date(data).toLocaleDateString("it-IT");

  if (!stimata) {
    return <span className={`text-sm tabular-nums text-ink/60 ${className}`}>{testo}</span>;
  }

  return (
    <span
      title={segnaposto ? "Mese previsto — nessun giorno concordato" : "Data prevista — non ancora avvenuta"}
      className={`flex items-center gap-1 text-sm italic tabular-nums text-ink/45 ${className}`}
    >
      <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {testo}
    </span>
  );
}
